import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, ExternalLink } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTabNavigate } from "@/hooks/useTabNavigate";
import { format } from "date-fns";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

interface Props {
  open: boolean;
  onClose: () => void;
  entityType: string;
  syncTime: string;
  importedCount: number;
}

const SYNC_BADGE: Record<string, { label: string; className: string }> = {
  synced: { label: "Synced", className: "bg-emerald-100 text-emerald-700" },
  pending: { label: "Pending", className: "bg-amber-100 text-amber-700" },
  error: { label: "Error", className: "bg-red-100 text-red-700" },
  not_synced: { label: "Not Synced", className: "" },
};

interface DisplayRecord {
  id: string;
  primary: string;
  secondary: string;
  amount?: number;
  qbId: string;
  syncStatus?: string;
  number?: number | string;
  link: string;
}

export function SyncLogDetailModal({ open, onClose, entityType, syncTime, importedCount }: Props) {
  const navigate = useTabNavigate();

  const { data: records = [], isLoading } = useQuery({
    queryKey: ["sync_detail", entityType, syncTime],
    queryFn: async (): Promise<DisplayRecord[]> => {
      switch (entityType) {
        case "account": {
          const { data, error } = await supabase
            .from("chart_of_accounts")
            .select("*")
            .order("account_number");
          if (error) console.error("account query error:", error);
          return (data ?? [])
            .filter((r: any) => r.qb_account_id)
            .map((r: any) => ({
              id: r.id,
              primary: r.account_number ? `${r.account_number} — ${r.name}` : r.name,
              secondary: r.account_type ?? "—",
              qbId: r.qb_account_id,
              link: "/controller/settings?tab=coa",
            }));
        }
        case "customer": {
          const { data, error } = await supabase
            .from("customers")
            .select("*")
            .eq("is_deleted", false)
            .order("name");
          if (error) console.error("customer query error:", error);
          return (data ?? [])
            .filter((r: any) => r.qb_customer_id)
            .map((r: any) => ({
              id: r.id,
              primary: r.name,
              secondary: [r.contact_name, r.email, r.phone].filter(Boolean).join(" · ") || "—",
              qbId: r.qb_customer_id,
              syncStatus: r.qb_sync_status,
              number: r.customer_number,
              link: `/controller/customers/${r.id}`,
            }));
        }
        case "estimate": {
          const { data, error } = await supabase
            .from("estimates")
            .select("*, customers(name)")
            .order("created_at", { ascending: false });
          if (error) console.error("estimate query error:", error);
          return (data ?? [])
            .filter((r: any) => r.qb_estimate_id)
            .map((r: any) => ({
              id: r.id,
              primary: r.estimate_number || r.job_name || "—",
              secondary: (r.customers as any)?.name ?? r.job_name ?? "—",
              amount: Number(r.total_amount ?? r.total ?? 0),
              qbId: r.qb_estimate_id,
              link: `/controller/estimates/${r.id}`,
            }));
        }
        case "invoice": {
          const { data, error } = await supabase
            .from("invoices")
            .select("*, customers(name), projects(name, project_number)")
            .is("deleted_at", null)
            .order("invoice_date", { ascending: false });
          if (error) console.error("invoice query error:", error);
          return (data ?? [])
            .filter((r: any) => r.qb_invoice_id)
            .map((r: any) => ({
              id: r.id,
              primary: r.invoice_number,
              secondary: (r.projects as any)?.name
                ? `${(r.projects as any).project_number ? (r.projects as any).project_number + " — " : ""}${(r.projects as any).name}`
                : (r.customers as any)?.name ?? "—",
              amount: Number(r.total ?? 0),
              qbId: r.qb_invoice_id,
              link: r.project_id
                ? `/controller/projects/${r.project_id}?tab=invoices`
                : r.customer_id
                ? `/controller/customers/${r.customer_id}?tab=billing`
                : `/controller/invoices`,
            }));
        }
        case "expense": {
          // QB expenses are imported into the bills table with source='qb_import'
          const { data, error } = await supabase
            .from("bills")
            .select("*, vendors(name), projects(name)")
            .eq("source", "qb_import")
            .order("bill_date", { ascending: false });
          if (error) console.error("expense query error:", error);
          return (data ?? []).map((r: any) => ({
            id: r.id,
            primary: r.bill_number || "—",
            secondary: (r.vendors as any)?.name ?? (r.projects as any)?.name ?? r.description ?? "—",
            amount: Number(r.total ?? 0),
            qbId: r.bill_number,
            link: r.project_id
              ? `/controller/projects/${r.project_id}?tab=work_orders`
              : `/controller/bills`,
          }));
        }
        case "vendor": {
          const { data, error } = await supabase
            .from("vendors")
            .select("*")
            .eq("is_deleted", false)
            .order("name");
          if (error) console.error("vendor query error:", error);
          return (data ?? [])
            .filter((r: any) => r.qb_vendor_id)
            .map((r: any) => ({
              id: r.id,
              primary: r.name,
              secondary: [r.contact_name, r.email, r.phone].filter(Boolean).join(" · ") || "—",
              qbId: r.qb_vendor_id,
              syncStatus: r.qb_sync_status,
              link: `/controller/vendors`,
            }));
        }
        default:
          return [];
      }
    },
    enabled: open && !!entityType,
  });

  const entityLabel = entityType === "account" ? "Accounts" : entityType === "customer" ? "Customers" : entityType === "vendor" ? "Vendors" : entityType === "estimate" ? "Estimates" : entityType === "invoice" ? "Invoices" : "Expenses";
  const hasAmount = ["estimate", "invoice", "expense"].includes(entityType);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{entityLabel} from QuickBooks</DialogTitle>
          <DialogDescription>
            {importedCount} records synced · Last updated {format(new Date(syncTime), "MMM d, yyyy h:mm a")}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : records.length === 0 ? (
          <p className="text-center py-10 text-muted-foreground">No records found.</p>
        ) : (
          <div className="border rounded-md overflow-hidden max-h-[60vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {entityType === "customer" && <TableHead className="w-16">#</TableHead>}
                  <TableHead>Name / Description</TableHead>
                  <TableHead>Details</TableHead>
                  {hasAmount && <TableHead className="text-right">Amount</TableHead>}
                  <TableHead>QB ID</TableHead>
                  {entityType === "customer" && <TableHead>Sync</TableHead>}
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((r) => (
                  <TableRow key={r.id} className="hover:bg-muted/50">
                    {entityType === "customer" && (
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {r.number ?? "—"}
                      </TableCell>
                    )}
                    <TableCell className="font-medium text-sm">{r.primary}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{r.secondary}</TableCell>
                    {hasAmount && (
                      <TableCell className="text-right font-medium text-sm">{fmt(r.amount!)}</TableCell>
                    )}
                    <TableCell>
                      <span className="font-mono text-xs text-muted-foreground">{r.qbId}</span>
                    </TableCell>
                    {entityType === "customer" && (
                      <TableCell>
                        {r.syncStatus && (
                          <Badge variant="outline" className={`text-[10px] ${SYNC_BADGE[r.syncStatus]?.className ?? ""}`}>
                            {SYNC_BADGE[r.syncStatus]?.label ?? r.syncStatus}
                          </Badge>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => { onClose(); navigate(r.link); }}
                        title="View in app"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <span className="text-xs text-muted-foreground">{records.length} records</span>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
